import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Col, Descriptions, Input, Modal, Row, Space, Spin, Statistic, Table, Tag, message
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import api from '../../services/api';

const PLAN_CATEGORY_ORDER = [
  { key: 'major', label: '公司事项' },
  { key: 'major_sub', label: '公司事项子任务' },
  { key: 'dept', label: '部门事项' },
  { key: 'dept_sub', label: '部门事项子任务' },
  { key: 'dept_task', label: '部门任务' }
];

export default function ResignationHandoverModal({
  open,
  processId,
  employeeName,
  onCancel,
  onSuccess
}) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [targetUser, setTargetUser] = useState(null);

  const loadPreview = useCallback(async () => {
    if (!processId) return;
    setLoading(true);
    try {
      const res = await api.get(`/hr/processes/${processId}/resignation-handover/preview`);
      if (res.data.success) {
        setPreview(res.data.data?.preview || null);
      }
    } catch (err) {
      message.error(err.response?.data?.message || '加载交接预览失败');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [processId]);

  useEffect(() => {
    if (open) {
      setKeyword('');
      setSearchResults([]);
      setTargetUser(null);
      loadPreview();
    }
  }, [open, loadPreview]);

  const searchUsers = async () => {
    const q = keyword.trim();
    if (!q) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get('/hr/processes/resignation-handover/users', {
        params: { keyword: q, page: 1, pageSize: 20 }
      });
      if (res.data.success) {
        setSearchResults(res.data.data?.list || []);
      }
    } catch (err) {
      message.error(err.response?.data?.message || '搜索用户失败');
    } finally {
      setSearching(false);
    }
  };

  const workflow = preview?.workflow;
  const plan = preview?.plan;
  const totalOpen = preview?.total_open || 0;

  const planStats = useMemo(() => PLAN_CATEGORY_ORDER.map(({ key, label }) => ({
    key,
    label,
    count: plan?.[key]?.count || 0
  })), [plan]);

  const handleSubmit = async () => {
    if (!targetUser?.user_id) {
      message.warning('请选择交接目标');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/hr/processes/${processId}/resignation-handover`, {
        target_user_id: targetUser.user_id
      });
      if (res.data.success) {
        message.success(res.data.message || '交接办理成功');
        onSuccess?.(res.data.data);
      }
    } catch (err) {
      message.error(err.response?.data?.message || '交接办理失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={`办理交接${employeeName ? ` - ${employeeName}` : ''}`}
      open={open}
      onCancel={onCancel}
      width={920}
      destroyOnHidden
      footer={(
        <Space>
          <Button onClick={onCancel}>取消</Button>
          <Button type="primary" loading={submitting} disabled={!targetUser} onClick={handleSubmit}>
            确认交接
          </Button>
        </Space>
      )}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
      ) : (
        <>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="进行中发起">{workflow?.initiated_pending?.count || 0}</Descriptions.Item>
            <Descriptions.Item label="待审批">{workflow?.approver?.count || 0}</Descriptions.Item>
            <Descriptions.Item label="待阅抄送">{workflow?.cc?.count || 0}</Descriptions.Item>
            <Descriptions.Item label="待执行">{workflow?.executor?.count || 0}</Descriptions.Item>
          </Descriptions>

          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            {planStats.map(({ key, label, count }) => (
              <Col xs={12} sm={8} md={6} key={key}>
                <Statistic title={label} value={count} />
              </Col>
            ))}
            <Col xs={12} sm={8} md={6}>
              <Statistic title="待交接合计" value={totalOpen} valueStyle={{ color: totalOpen > 0 ? '#fa8c16' : undefined }} />
            </Col>
          </Row>

          {totalOpen === 0 && (
            <Alert type="warning" showIcon message="该员工暂无进行中的审批或计划负责项，仍可选择交接人完成登记。" style={{ marginBottom: 16 }} />
          )}
        </>
      )}

      <div style={{ marginBottom: 8, fontWeight: 500 }}>选择交接目标</div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          allowClear
          placeholder="姓名 / 账号 / 工号"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onPressEnter={searchUsers}
          style={{ width: 260 }}
        />
        <Button icon={<SearchOutlined />} loading={searching} onClick={searchUsers}>搜索</Button>
      </Space>

      {searchResults.length > 0 && (
        <Table
          rowKey="user_id"
          size="small"
          pagination={false}
          style={{ marginBottom: 12 }}
          dataSource={searchResults}
          rowClassName={(row) => (row.user_id === targetUser?.user_id ? 'ant-table-row-selected' : '')}
          columns={[
            { title: '姓名', dataIndex: 'real_name', width: 120 },
            { title: '账号', dataIndex: 'username', width: 120 },
            { title: '工号', dataIndex: 'employee_no', width: 120, render: (v) => v || '—' },
            { title: '部门', dataIndex: 'dept_name', ellipsis: true, render: (v) => v || '—' },
            {
              title: '操作',
              width: 90,
              render: (_, row) => (
                <Button
                  type={row.user_id === targetUser?.user_id ? 'primary' : 'link'}
                  size="small"
                  onClick={() => setTargetUser(row)}
                >
                  {row.user_id === targetUser?.user_id ? '已选' : '选择'}
                </Button>
              )
            }
          ]}
        />
      )}

      {targetUser && (
        <div style={{ marginTop: 8 }}>
          已选择：
          <Tag color="blue">{targetUser.real_name || targetUser.username}</Tag>
          {targetUser.employee_no && <Tag>{targetUser.employee_no}</Tag>}
        </div>
      )}
    </Modal>
  );
}
